import { useTheme } from "styled-components";
import useGetCountry from "hooks/useGetCountry";
import { LISTING_FILTERS_PLACEHOLDER_OPTIONS_COUNT } from "utils/constants";
import { labelHelperInternal } from "helpers/textHelpers";
import { formatNumber } from "./numberHelper";
import {qsStringify} from "helpers/globalHelpers";

const reg = /^\/(ar|en)/;
const validateLang = (lang = null) => {
  if (["ar", "en"].indexOf(lang) === -1) lang = null;
  return lang;
};

export const getLang = () => {
  if (typeof window !== "undefined" && window.location) {
    if (window.location.pathname.match(reg)) {
      if(window.location.pathname.match(reg)[1].length > 2){
        const langArr = window.location.pathname.match(reg)[1].split("-")
        return langArr[0]
      }
      return window.location.pathname.match(reg)[1];
    } else {
      return "ar";
    }
  }
  return useTheme().locale;
};

export const getLang2 = () => {
  const lang1 = getLang();
  return lang1 === "ar" ? "en" : "ar";
};

export const capitalized = (lbl) => {
  return (lbl || "").replace(
    /\w+/g,
    (a) => a[0].toUpperCase() + a.substring(1)
  );
};

export const labelHelper = (
  object?: any,
  locale: string = null,
  specific_field: string = null
) => {
  locale = locale || getLang();
  return labelHelperInternal(locale, object, specific_field);
};

export const labelHelperSeoValue = (
  object?: any
) => {
  return labelHelper(object, getLang(), "seo_value");
};

export const trField = (object: {}, field = null, lang = null) => {
  if (!object) return "";
  lang = validateLang(lang);
  const locale = lang || getLang();
  return object[`${field}_${locale}`] || object[field] || "";
};

export const urlHelper = (object: {}, lang = null): string => {
  if (!object) return "";
  lang = validateLang(lang);
  return [
    ...new Set([
      trField(object, "parent_url", lang),
      trField(object, "url_name", lang) || trField(object, "url", lang),
    ]),
  ]
    .filter(Boolean)
    .join("/");
};

export const currencyHelper = (object: {}) => {
  if (!object) return "";
  return trField(object, "currency");
};

export const imageHelper = (relativePath?: string, crop?: string) => {
  if (relativePath?.includes("youtube")) {
    const youtubeImagesParts = relativePath.split("https://").filter(Boolean);
    const finalImage = youtubeImagesParts[1] || youtubeImagesParts[0];
    return ("https://" + finalImage)
      .replace(".webp", "")
      .replace(".jpg.jpg", ".jpg");
  }
  if (!relativePath) return null;
  relativePath = relativePath.replace(
    "https://arbok-new.sooqtest.com",
    process.env.NEXT_PUBLIC_IMAGES_CDN
  );
  if (
    relativePath.indexOf("https://") === 0 ||
    relativePath.indexOf("//") === 0 ||
    relativePath[0] === "/"
  ) {
    let absPath = relativePath;
    // in case the path is absolute
    // ex: https://opensooq-images.os-cdn.com/previews/700x800/10/50/xxxxxxxxx.jpg.webp?
    // output   https://opensooq-images.os-cdn.com/previews/{crop}/10/50/xxxxxxxxx.jpg.webp
    if (crop) {
      absPath = absPath
        .replace(/previews\/[^/]+\//, `previews/${crop}/`)
        .replace(/\.(jpg|webp)$/g, ".webp");
    }
    return absPath;
  } else {
    // in case the path is relative
    // ex: 10/50/xxxxxxxxx.jpg
    // output   https://opensooq-images.os-cdn.com/previews/{crop}/10/50/xxxxxxxxx.jpg.webp
    let s = `${process.env.NEXT_PUBLIC_IMAGES_CDN}/${
      crop ? `previews/${crop}/` : ""
    }${relativePath}${crop ? ".webp" : ""}`;
    return s;
  }
};

export const trHelper = (text, characters) => {
  for (const property in characters) {
    text = text.replace(property, characters[property]);
  }
  return text;
};

export function classNames(...args) {
  let output = [];
  for (let i = 0; i < args.length; i++) {
    if (args[i]) output.push(args[i]);
  }
  return { className: output.join(" ") };
}

export const apiUrlNameHelper = (context, object: {}) => {
  if (!object) return "";
  return object[`url_name_${context.lang}`].trim() || object[`url_name`].trim();
};

export const appendCurrencyHelper = (
  price: number,
  currency: string,
  item?: any
) => {
  const currentCountry = useGetCountry();
  const { translations } = useTheme();
  currency = currency || trField(currentCountry, "currency");
  return price
    ? `${formatNumber(price)} ${currency}`
    : `${translations.notAvailable}`;
};

export const generatePlaceholder = (label, selectedOps, fieldName = null) => {
  if (selectedOps?.length === 0) return null;
  let fieldLabel = fieldName ? fieldName + " : " : "";
  if (label === "range") {
    if (!selectedOps[0] && !selectedOps[1]) return null;
    return `${fieldLabel}${selectedOps[0] ? selectedOps[0] : "∞"} - ${
      selectedOps[1] ? selectedOps[1] : "∞"
    } ${selectedOps[3] ? selectedOps[3] : ""}`;
  }

  if (selectedOps.length < LISTING_FILTERS_PLACEHOLDER_OPTIONS_COUNT) {
    let labelHelperFn = labelHelper;
    if (selectedOps[0].fieldName == "Rooms" || selectedOps[0].fieldName == "Bathrooms") {
      labelHelperFn = labelHelperSeoValue;
    }
    return fieldLabel + selectedOps.map((obj) => labelHelperFn(obj)).join(" , ");
  } else {
    return `${fieldLabel} ${selectedOps.length} - ${label}`;
  }
};

export const maskPhoneNumberHelper = (
  phoneNumber: string,
  numberOfMaskedChars: number
) => {
  phoneNumber = phoneNumber.slice(0, -numberOfMaskedChars);
  for (let x = 0; x < numberOfMaskedChars; x++) {
    phoneNumber += "X";
  }
  return phoneNumber;
};

export const urlHelperWithoutCustomParam = (
  object: any,
  lang = null
): string => {
  if (!object) return "";
  lang = validateLang(lang);
  return (
    [
      ...new Set([
        trField(object, "parent_url", lang) || trField(object, "parent_url_name", lang),
        !(object?.field_is_linkable == 0) ?  trField(object, "url_name", lang) || trField(object, "url", lang) : null,
      ]),
    ]
      .filter(Boolean)
      .join("/")
  );
};
export const urlHelperWithCustomParam = (object: any, lang = null): string => {
  if (!object) return "";
  lang = validateLang(lang);
  return object?.field_is_linkable == 0 ?
   (
      `${[object.field_name]}=${object.id}`
  ) : "";
};

export const replacePlaceHoldersForCarsLanding = (textWithPlaceHolder, city, country, locale) => {
  return textWithPlaceHolder.replace(
      "__market_name__",
      labelHelper(city || country, locale)
  );
}